import { useContext, useState } from "react";
import { useDispatch, useSelector } from "store";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import cartApi from "store/slices/cart/cartApi";
import { addArticleToCart, setSessionId } from "store/slices/cart/cartSlice";
import generateCartArticle from "helpers/article/generateCartArticle";
import { notify } from "utils/SnackbarProvider";
import {
  selectHomeServiceArea,
  selectSessionId,
  selectAllServiceVariants,
  selectAllServices,
  selectAllPackages,
  selectPackageVariantWithServicesById,
  PackageVariantWithServiceVariants,
} from "store/selectors";
import { slotsApi } from "pages/BookingPage/BookingSlotPicker/slotPickerHelpers";
import OutletBookingContext, { HomeServiceBookingModals } from "pages/OutletPage/outletContext";
import { TYPES } from "pages/OutletPage/constants";

// Define types for the props
interface UseBookingHandlersProps {
  outlet: { id: number };
}

// Define types for the return values
interface UseBookingHandlersReturn {
  handleBookingFirstService: (type?: TYPES, id?: number) => void;
  handleBookingOtherService: (
    type?: TYPES,
    id?: number,
    selectedPurchasablePackageVariant?: PackageVariantWithServiceVariants
  ) => Promise<void>;
  setIsLoadingDraft: (value: boolean) => void;
  fetchAvailability: () => Promise<void>;
  isLoadingDraft: boolean;
  isLoadingAvailability: boolean;
}

// Hook implementation
export default function useBookingHandlers({
  outlet,
}: UseBookingHandlersProps): UseBookingHandlersReturn {
  const {
    selectedPurchasable,
    selectedPurchasablePackage,
    selectedPurchasableNumberOfPeople,
    selectedDate: date,
    setActiveBookingModal,
    setSelectedPurchasable,
    setSelectedPurchasablePackage,
    setSelectedPurchasableAvailability,
  } = useContext(OutletBookingContext);
  const { t } = useTranslation(["bookingPage", "outletPage"]);
  const dispatch = useDispatch();

  const selectedArea = useSelector(selectHomeServiceArea);
  const sessionId = useSelector(selectSessionId);
  const variants = useSelector(selectAllServiceVariants);
  const services = useSelector(selectAllServices);
  const packages = useSelector(selectAllPackages);

  const selectedPurchasablePackageVariant = useSelector(
    selectPackageVariantWithServicesById(selectedPurchasablePackage || 0)
  );

  const [isLoadingDraft, setIsLoadingDraft] = useState<boolean>(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);

  const fetchAvailability = async (type?: TYPES, id?: number): Promise<void> => {
    setIsLoadingAvailability(true);
    if (!selectedArea || !date || !selectedPurchasableNumberOfPeople) {
      setIsLoadingAvailability(false);
      return;
    }

    try {
      const { data } = await (type === TYPES.PACKAGES || selectedPurchasablePackage
        ? slotsApi.getHomeServicePackageSlots({
            area: selectedArea,
            peopleCount: selectedPurchasableNumberOfPeople,
            date: date.toFormat("yyyy-MM-dd"),
            packageVariantId: id ?? selectedPurchasablePackage ?? 0,
          })
        : slotsApi.getHomeServiceSlots({
            area: selectedArea,
            peopleCount: selectedPurchasableNumberOfPeople,
            date: date.toFormat("yyyy-MM-dd"),
            serviceVariantId: id ?? selectedPurchasable ?? 0,
          }));

      if (data.slots && data.slots.length > 0) {
        setSelectedPurchasableAvailability(data.slots);
        setActiveBookingModal(HomeServiceBookingModals.Availability);
      } else {
        notify(t("outletPage:notAvailable"), "error");
      }
    } catch (error: any) {
      if (error.response?.data?.detail) {
        notify(error.response.data.detail, "error");
      } else {
        notify(t("outletPage:notAvailable"), "error");
      }
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const handleBookingFirstService = (type?: TYPES, id?: number): void => {
    if (setSelectedPurchasableAvailability) {
      setSelectedPurchasableAvailability(null);
    }
    fetchAvailability(type, id);
  };

  const handleBookingOtherService = async (
    type?: TYPES,
    id?: number,
    selectedPurchasablePackageVariant?: PackageVariantWithServiceVariants
  ): Promise<void> => {
    if (setSelectedPurchasableAvailability) {
      setSelectedPurchasableAvailability(null);
    }
    await addOtherServiceToCart(type, id, selectedPurchasablePackageVariant);
  };

  const addOtherServiceToCart = async (
    type?: TYPES,
    id?: number,
    purchasablePackageVariant?: PackageVariantWithServiceVariants
  ): Promise<void> => {
    setIsLoadingDraft(true);
    try {
      const { data } = await cartApi.createDraftArticle({
        area: selectedArea!,
        date: date.toFormat("yyyy-MM-dd"),
        peopleCount: selectedPurchasableNumberOfPeople!,
        serviceVariant:
          type === TYPES.SERVICES
            ? id
            : selectedPurchasablePackage
            ? undefined
            : selectedPurchasable!,
        packageVariant: type === TYPES.PACKAGES ? id : selectedPurchasablePackage || undefined,
        outlet: outlet.id,
        sessionId: sessionId!,
      });

      const draft = data?.[0];
      const articleVariant = variants.find((variant) => variant.id === (id || selectedPurchasable));
      const articleService = services.find((service) => service.id === articleVariant?.service);

      const articlePackage = packages.find(
        (pkg) =>
          pkg.id === selectedPurchasablePackageVariant?.package ||
          purchasablePackageVariant?.package
      );

      const startTimeString =
        draft?.start.slice(-1) === "Z" ? draft?.start.slice(0, -1) : draft?.start;

      const cartArticle =
        selectedPurchasablePackage || type === TYPES.PACKAGES
          ? generateCartArticle(
              DateTime.fromISO(startTimeString),
              0,
              null,
              articlePackage?.depositPercentage || 0,
              undefined,
              selectedPurchasablePackageVariant || purchasablePackageVariant,
              selectedPurchasableNumberOfPeople || undefined
            )
          : generateCartArticle(
              DateTime.fromISO(startTimeString),
              [],
              null,
              articleService?.depositPercentage || 0,
              articleVariant,
              undefined,
              selectedPurchasableNumberOfPeople || undefined
            );

      if (!cartArticle) return;

      setIsLoadingDraft(false);
      setSelectedPurchasable(null);
      if (selectedPurchasablePackage) {
        setSelectedPurchasablePackage(null);
      }
      dispatch(setSessionId(data?.[0]?.sessionId));
      dispatch(
        addArticleToCart({
          ...cartArticle,
          draftIds: data?.map((d) => d.id),
        })
      );
      setActiveBookingModal(null);
    } catch (e: any) {
      setIsLoadingDraft(false);
      if (e.response?.data?.detail) {
        notify(e.response.data.detail, "error");
      } else {
        notify(t("outletPage:notAvailable"), "error");
      }
    }
  };

  return {
    handleBookingFirstService,
    handleBookingOtherService,
    fetchAvailability,
    setIsLoadingDraft,
    isLoadingDraft,
    isLoadingAvailability,
  };
}
